// Клиент 360° — всё про компанию на одном экране.
//
// Зачем. Сегодня по клиенту ходят в четыре места: сделки, дебиторка, контакты, счета в Bitrix.
// На портале 55 такой сводный экран есть (`peleng-portal/src/worker.js:4422` clientCard
// и `:4499` clientFleet), и он же показывает главное — не только «сколько должны», но и
// «каким флотом клиент владеет и по каким судам мы с ним ещё не работали». Смысл повторяем,
// данные берём из ядра.
//
// Экран ТОЛЬКО ЧИТАЮЩИЙ. Ни одной формы правки здесь нет и не должно быть: запись идёт в
// Bitrix, где бизнес-процессы раздают задания живым людям.
//
// Главное правило этого файла — НИ ОДНОЙ СВОЕЙ ФОРМУЛЫ.
//   • долг и просрочка приходят от того же источника, что в разделе «Дебиторка», и решает
//     ОБЩИЙ переключатель CRM (api/istochnik.ts): по умолчанию — своя схема (recv_own_api
//     считает корзины SQL-агрегатом, единица — непогашенный счёт), ?src=mirror — зеркало 134,
//     где расчёт остатка делает lib/payments.ts по загруженной странице,
//   • колонки и суммы счетов — в lib/smart.ts (тем же кодом, что раздел смарт-процессов),
//     а сам список счетов подчиняется тому же переключателю: под own он читается из
//     своей схемы через api/smartOwn (/api/docs/list?src=own), под ?src=mirror — зеркало,
//   • «оплачен ли счёт» — у своей схемы по дате оплаты из карточки счёта (paid_date,
//     перенесён из ufCrm_61C5B58222E68), у зеркала — по кодам стадий из lib/dashboard.ts
//     (тем же списком, что кабинет руководителя): семантику стадий типа 31 в Bitrix не
//     расставили, и по ней все счета выглядят открытыми — подробности там же, у кодов.
// Иначе цифры на карточке клиента разойдутся с разделами, и вопрос «какой из них верить»
// станет неразрешимым.
//
// Второе правило — честность про полноту. Ядро отдаёт страницами по 50 и агрегатов не умеет.
// Точно известны только СЧЁТЧИКИ (их ядро считает отдельным запросом), а любая СУММА здесь
// посчитана по загруженной странице. Везде, где это так, на экране это написано.
//
// ── Исключение из второго правила: банк ──
// Блок «Деньги от клиента в банке» устроен наоборот: `pbh_api` считает `totals` по ВСЕЙ
// выборке одним SQL. Это единственная точная сумма на экране, и она подписана именно так —
// иначе рядом с долгом, посчитанным по одной загруженной странице, её прочитают как такую же
// приблизительную.
//
// ── Границы у всего разные ──
// Bitrix — возраст 5 минут, история сделок с 28.02.2017. Банковская выписка — своя
// история: первый и последний день по контрагенту показаны в самом блоке, потому что
// «приходов от него не было» вне этих дней означает «выписки за те дни у нас нет».

import { useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { useЗадания } from '../api/bp'
import { useРазрывы } from '../api/chain'
import { useСтрокиПроверки } from '../api/dq'
import type { ОтветСтрокСужением } from '../api/dq'
import {
  useCompany, useContacts, useDeals, useFields, useFieldsQuery, useItems, usePayments, useRefs,
  useRightsApi,
} from '../api/hooks'
import { источник } from '../api/istochnik'
import { useВыписка } from '../api/pbh'
import { useРасхождения } from '../api/products'
import { useRecvOwnList, useRecvOwnSummary } from '../api/recvOwn'
import { ПОЛЯ_ПЕРЕНОСА, оговоркаИсточника, откудаСмарт } from '../api/smartOwn'
import type { Contact, Deal, ListParams } from '../api/types'
import { useДокументыКлиента } from '../api/ut'
import { EntityLink } from '../components/EntityLink'
import { EntityPicker } from '../components/EntityPicker'
import { FilterBar } from '../components/FilterBar'
import { PaymentsTable } from '../components/PaymentsTable'
import { QueryState } from '../components/QueryState'
import { SmartItemsTable } from '../components/SmartItemsTable'
import {
  ИНН_КОМПАНИИ, ИНН_ПОДПИСЬ, ИНН_ПОДПИСЬ_В_BITRIX, разобратьИНН,
} from '../lib/client360'
import type { ЧтоСИНН } from '../lib/client360'
import {
  INVOICE_PAID_CODES, stageIdsByCode, разрывыПоВидам, разрывыРубли,
} from '../lib/dashboard'
import { классСтепени } from '../lib/dq'
import {
  DEAL_VESSEL_FIELD, VESSEL_FIELDS, contactName, entityPath, isVesselContact, vesselName,
} from '../lib/entities'
import type { EntityRef } from '../lib/entities'
import { firstValue, fmtDate, fmtDays, fmtMoney, fmtText, plural, pluralN } from '../lib/format'
import { сколькоСделок } from '../lib/products'
import { toNumber } from '../lib/money'
import { PAID_CATEGORY_ID, PAY, PAY_LIST_SELECT, paymentView, summarize } from '../lib/payments'
import type { SmartColumn } from '../lib/smart'
import {
  SYSTEM_SELECT, buildColumns, moneyTotals, refId, smartEntity, vesselRefId,
} from '../lib/smart'

/** Счета клиенту. Тип живёт в SMART_TYPES (lib/smart.ts) — здесь только его номер. */
const INVOICE_TYPE = 31

/**
 * Код поля «дата оплаты» счёта. Взят из карты переноса (api/smartOwn.ПОЛЯ_ПЕРЕНОСА),
 * а не написан руками: перенос читал дату оплаты ИЗ этого кода Bitrix, когда заполнял
 * paid_at своей схемы, и под этим же кодом записьИзДокумента возвращает её на экран.
 * Второй копии кода поля в этом файле быть не должно — разойдутся.
 */
const ДАТА_ОПЛАТЫ_СЧЁТА = ПОЛЯ_ПЕРЕНОСА[INVOICE_TYPE].оплачен[0]

/** Дата документа счёта (begindate) — по ней own-ветка сортирует «новые сверху». */
const ДАТА_СЧЁТА = ПОЛЯ_ПЕРЕНОСА[INVOICE_TYPE].дата[0]

/** Сколько связанных строк показываем в карточке. Полные списки — в своих разделах. */
const RELATED = 10
/** Страница ядра. Всё, что «по загруженной странице», ограничено этим числом. */
const PAGE = 50

/** Сумма сделок по загруженным строкам. Пустая сумма — это не ноль, но в итог она не идёт. */
const sumDeals = (rows: Deal[]) =>
  rows.reduce((s, d) => s + (toNumber(d.OPPORTUNITY) ?? 0), 0)

/** Есть ли в выборке валюта, отличная от рубля: складывать такие суммы в одну нельзя. */
const hasOtherCurrency = (rows: Deal[]) =>
  rows.some((d) => String(d.CURRENCY_ID || 'RUB') !== 'RUB')

/**
 * Судно из сделки. Поле-привязка иногда приходит массивом — Bitrix так умеет, а «судна нет»
 * он пишет нулём. И то и другое чистит `vesselRefId`; он же снимает приставку `C_` — у 54
 * сделок в этом поле стоит `C_19795`, а не число (подробности у самой функции в lib/smart.ts).
 * Без неё такой id уезжал в `crm.contact.list` списком ID и не находил ничего: судно
 * попадало во флот строкой «#C_19795» и вело на несуществующий адрес.
 */
function dealVesselId(d: Deal): string {
  return vesselRefId(d[DEAL_VESSEL_FIELD])
}

export default function Client360() {
  const [sp, setSp] = useSearchParams()
  // Компания живёт в адресе: ссылку «клиент 360» можно переслать коллеге, и она откроется
  // на том же клиенте. Так же сделано в разделе «Дебиторка».
  const companyId = sp.get('company') || ''

  // Название для поля выбора берём тем же запросом, каким тело карточки читает компанию
  // (ключ кэша ['company', id] один) — иначе на экране было бы два crm.company.get подряд.
  const company = useCompany(companyId || undefined)
  const value: EntityRef | null = companyId
    ? { kind: 'company', id: companyId, title: company.data?.TITLE || `#${companyId}` }
    : null

  const change = (v: EntityRef | null) => {
    const next = new URLSearchParams(sp)
    if (v) next.set('company', v.id)
    else next.delete('company')
    setSp(next, { replace: true })
  }

  return (
    <>
      <div className="page-head">
        <h1>Клиент 360°</h1>
        <div className="spacer" />
        {companyId && <Link to={entityPath('company', companyId)}>карточка компании →</Link>}
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <FilterBar id="client360">
          <div style={{ flex: 2, minWidth: 260 }}>
            <EntityPicker kind="company" id="c360-company" label="Клиент"
                          value={value} onChange={change} />
          </div>
        </FilterBar>
        <div className="hint" style={{ marginTop: 10 }}>
          Всё про клиента в одном месте: сделки, долг, контакты, флот и счета. Только чтение —
          править данные по-прежнему в Bitrix, там на изменения завязаны бизнес-процессы.
        </div>
      </div>

      {/* Тело вынесено в отдельный компонент намеренно: пока клиент не выбран, ни один запрос
          к ядру не уходит. Хуки нельзя вызывать по условию, а десяток запросов с пустым
          фильтром — это «отдай всё, что есть» на 74 тысячи сделок. */}
      {companyId
        ? <ClientBody companyId={companyId} />
        : (
          <div className="card">
            <div className="muted">
              Выберите клиента выше. Экран собирает данные из ядра: компанию и её поля, сделки,
              дебиторку (свою схему CRM, а при ?src=mirror — смарт-процесс 134), контакты,
              суда из сделок и счета (смарт-процесс 31).
            </div>
          </div>
        )}
    </>
  )
}

function ClientBody({ companyId }: { companyId: string }) {
  const refs = useRefs()
  const f = useFields('company')

  const company = useCompany(companyId)

  // ─── сделки ───
  // Два запроса, а не один с подсчётом на клиенте. Причина: счётчик `total` ядро считает
  // отдельным SQL по всей выборке, то есть «сколько всего» и «сколько открытых» получаются
  // ТОЧНЫМИ, а не «сколько нашлось на первой странице». Суммы так не получить — их считаем
  // по странице и подписываем.
  const dealsAll = useDeals({
    filter: { COMPANY_ID: companyId },
    order: { DATE_MODIFY: 'DESC' },
    select: ['ID', 'TITLE', 'STAGE_ID', 'CATEGORY_ID', 'OPPORTUNITY', 'CURRENCY_ID',
             'CLOSED', 'DATE_MODIFY', DEAL_VESSEL_FIELD],
  })
  const dealsOpen = useDeals({
    filter: { COMPANY_ID: companyId, CLOSED: 'N' },
    order: { DATE_MODIFY: 'DESC' },
    select: ['ID', 'OPPORTUNITY', 'CURRENCY_ID'],
  })

  const dealRows = useMemo(() => dealsAll.data?.items ?? [], [dealsAll.data])
  const openRows = dealsOpen.data?.items ?? []
  const portfolio = sumDeals(openRows)

  // ─── дебиторка ───
  // ДВА ИСТОЧНИКА, как в разделе «Дебиторка», и решает ОБЩИЙ переключатель CRM
  // (api/istochnik.ts): по умолчанию — своя схема (/api/recv-own, recv_own_api ядра),
  // ?src=mirror — зеркало 134, как было. Единицы счёта РАЗНЫЕ: зеркало перечисляет
  // ЗАПИСИ смарт-процесса «Платежи», своя схема — НЕПОГАШЕННЫЕ СЧЕТА (сумма счёта
  // минус платежи с датой оплаты), и подписи блока обязаны называть свою.
  //
  // Хуки обеих веток объявлены ВСЕГДА (порядок хуков ломать нельзя), но запрос уходит
  // только у выбранной: у второй enabled=false, и в сеть она не ходит вовсе.
  // Предохранитель зоны, как у сделок, здесь не нужен: /api/recv-own сужает выборку
  // зоной сам — BFF подставляет `users=` из прав (web/bff/main.py, ручка recv_own).
  const дебиторкаСвоя = источник() === 'own'
  // Зеркало: отбор ровно тот же, что в карточке компании и в разделе «Дебиторка» —
  // пустая дата оплаты и воронка не «Оплачено». Расчёт остатка — paymentView/summarize.
  const payments = usePayments({
    filter: { companyId, [PAY.paidDate]: '', '!categoryId': PAID_CATEGORY_ID },
    order: { [PAY.dueDate]: 'ASC' },
    select: PAY_LIST_SELECT,
  }, !дебиторкаСвоя)
  const payRows = useMemo(
    () => (payments.data?.items ?? []).map((p) => paymentView(p, refs)),
    [payments.data, refs],
  )
  const payTotals = useMemo(() => summarize(payRows), [payRows])
  // Своя схема: корзины и долг считает ядро SQL-агрегатом ПО ВСЕЙ выборке клиента —
  // в отличие от зеркальной ветки, это ТОЧНЫЕ суммы, а не «по загруженной странице».
  const recvСводка = useRecvOwnSummary(
    useMemo(() => ({ company: companyId }), [companyId]), дебиторкаСвоя,
  )
  const recvСписок = useRecvOwnList(
    useMemo(() => ({ company: companyId, sort: 'due' as const }), [companyId]), дебиторкаСвоя,
  )
  const своднаяOwn = recvСводка.data
  const просрочкаOwn = своднаяOwn?.buckets.find((b) => b.bucket === 'overdue')
  const строкиOwn = recvСписок.data?.rows ?? []

  // ─── контакты компании ───
  // Суда в Bitrix — тоже контакты, и они приходят этим же запросом. Разделяем: людям место
  // в «Контактах», судам — в «Флоте», иначе список сотрудников разбавлен теплоходами.
  const contactParams = useMemo(() => ({
    filter: { COMPANY_ID: companyId } as Record<string, unknown>,
    select: ['ID', 'NAME', 'LAST_NAME', 'SECOND_NAME', 'POST', 'PHONE', 'EMAIL',
             VESSEL_FIELDS.imo],
  }), [companyId])
  const contacts = useContacts(contactParams)
  const people = (contacts.data?.items ?? []).filter((c) => !isVesselContact(c))
  const ownVessels = (contacts.data?.items ?? []).filter(isVesselContact)

  // ─── флот ───
  // Суда, встречающиеся в сделках клиента: в сделке судно лежит полем DEAL_VESSEL_FIELD.
  // Считаем по загруженной странице сделок — сколько их было, столько и просмотрели.
  const dealVessels = useMemo(() => {
    const m = new Map<string, number>()
    for (const d of dealRows) {
      const id = dealVesselId(d)
      if (id) m.set(id, (m.get(id) ?? 0) + 1)
    }
    return m
  }, [dealRows])

  const dealVesselIds = useMemo(() => [...dealVessels.keys()], [dealVessels])
  // Названия судов из сделок — ОДНИМ запросом со списком id (ядро понимает его как IN),
  // а не по запросу на строку: пятьдесят сделок дали бы пятьдесят запросов.
  //
  // Пока сделки не пришли, спрашивать нечего, а `enabled` у useContacts нет. Поэтому
  // подставляем ТЕ ЖЕ параметры, что у запроса контактов компании выше: ключ кэша совпадает,
  // и запрос в сеть не уходит вовсе. Пустой фильтр здесь означал бы «все контакты портала».
  const vesselContacts = useContacts(useMemo(() => (
    dealVesselIds.length
      ? { filter: { ID: dealVesselIds.slice(0, PAGE) }, select: contactParams.select }
      : contactParams
  ), [dealVesselIds, contactParams]))

  const fleet = useMemo(() => {
    const byId = new Map<string, { id: string; name: string; imo: string; deals: number }>()
    const put = (c: Contact, deals: number) => {
      const id = String(c.ID)
      const prev = byId.get(id)
      byId.set(id, {
        id,
        name: vesselName(c),
        imo: String(c[VESSEL_FIELDS.imo] ?? ''),
        deals: Math.max(prev?.deals ?? 0, deals),
      })
    }
    // суда, привязанные к компании как контакты (в сделках могли и не встречаться)
    for (const c of ownVessels) put(c, dealVessels.get(String(c.ID)) ?? 0)
    // суда из сделок (могут принадлежать другой компании — заказчик и судовладелец разные)
    for (const c of vesselContacts.data?.items ?? []) put(c, dealVessels.get(String(c.ID)) ?? 0)
    // id, по которым контакт не пришёл: судно удалено или закрыто правами — не прячем
    for (const [id, n] of dealVessels) {
      if (!byId.has(id)) byId.set(id, { id, name: `#${id}`, imo: '', deals: n })
    }
    return [...byId.values()].sort((a, b) => b.deals - a.deals || a.name.localeCompare(b.name, 'ru'))
  }, [ownVessels, vesselContacts.data, dealVessels])

  const vesselNameById = useMemo(
    () => new Map(fleet.map((v) => [v.id, v.name])), [fleet],
  )
  const untouched = fleet.filter((v) => v.deals === 0).length

  // ─── счета клиенту (смарт-процесс 31) ───
  // Коды полей НЕ зашиваем: состав полей у 31 свой и меняется в Bitrix без нас. Колонки
  // строит buildColumns по справочнику `/fields` — оттуда же приходят и человеческие
  // названия. Системные поля (сумма, стадия, сделка) известны из контракта ядра.
  const invFields = useFields(smartEntity(INVOICE_TYPE))
  // Тот же запрос, что внутри useFields (ключ кэша один) — нужен только его статус:
  // от справочника зависит состав `select`, и спрашивать счета ДО его прихода значит
  // сходить в ядро дважды, второй раз с другим набором полей.
  const invFieldsPending = useFieldsQuery(smartEntity(INVOICE_TYPE)).isLoading
  const invColumns = useMemo(() => buildColumns(invFields, { money: true }), [invFields])
  const invCols = useMemo<SmartColumn[]>(() => {
    // компания на этом экране одна — колонка была бы одним и тем же словом во всех строках
    const picked = invColumns.defaults.filter((c) => c !== 'companyId')
    // Сумму счёта колонкой даёт `money: true` у типа 31 (она лежит в стандартном
    // `opportunity` — это видно и по exec-панели, `worker.js:266`). Страховка ниже осталась:
    // счёт без единственной цифры, ради которой его открывают, — худший из возможных экранов.
    if (!picked.includes('opportunity')) {
      const at = picked.indexOf('stageId')
      picked.splice(at >= 0 ? at + 1 : picked.length, 0, 'opportunity')
    }
    return picked.map((c) => invColumns.byCode.get(c)).filter((c): c is SmartColumn => !!c)
  }, [invColumns])

  const invSelect = useMemo(
    () => [...SYSTEM_SELECT, ...invCols.filter((c) => !c.system).map((c) => c.code)],
    [invCols],
  )
  // ПАРАМЕТРЫ У ДВУХ ИСТОЧНИКОВ РАЗНЫЕ, и это не косметика.
  //   • Зеркало — как было всегда: сортировка по id, а не по дате счёта (дата счёта —
  //     это UF-поле, и зеркало сортирует его как текст; id убывает вместе с датой
  //     создания, поэтому «новые сверху» получается честно).
  //   • Своя схема сортировку по id не умеет вовсе (docs_api, ПОРЯДОК_СПИСКА_OWN),
  //     зато честно сортирует по дате ДОКУМЕНТА — под own заказываем её (begindate ↓,
  //     переводится в sort=date), и «новые сверху» получается по дате счёта.
  // До 30.07.2026 параметры были одни (id ↓), и под own запрос честно падал в зеркало.
  const invParamsOwn = useMemo<ListParams>(() => ({
    filter: { companyId },
    order: { [ДАТА_СЧЁТА]: 'DESC' },
    select: invSelect,
  }), [companyId, invSelect])
  const invParamsMirror = useMemo<ListParams>(() => ({
    filter: { companyId },
    order: { id: 'DESC' },
    select: invSelect,
  }), [companyId, invSelect])
  // Куда пойдёт запрос, решает api/smartOwn (единственное место решения — то же, что
  // у разделов /smart/*): под own — своя схема через /api/docs/list?src=own, а если own
  // недоступен (?src=mirror или зона учётки, которую своя схема пока не сужает) —
  // зеркало со старыми параметрами. Оговорка ниже печатается словами самого модуля.
  const счетаИзOwn = откудаСмарт(INVOICE_TYPE, invParamsOwn).из === 'own'
  const invParams = счетаИзOwn ? invParamsOwn : invParamsMirror
  const invoices = useItems(INVOICE_TYPE, invParams, !invFieldsPending)
  const оговоркаСчетов = оговоркаИсточника(INVOICE_TYPE, invParams)
  const invRows = useMemo(() => invoices.data?.items ?? [], [invoices.data])

  // Оплачен ли счёт — У ДВУХ ИСТОЧНИКОВ ПО-РАЗНОМУ, и оба способа названы на экране.
  //
  // СВОЯ СХЕМА (счетаИзOwn): по дате оплаты из карточки счёта (paid_at, наружу —
  // paid_date, на экран возвращается под кодом переноса ДАТА_ОПЛАТЫ_СЧЁТА). Стадий
  // Bitrix в своей схеме нет как данных, и судить по ним нечем и незачем: дата оплаты —
  // первичный факт, с которого перенос её и списал (ufCrm_61C5B58222E68). Замер на
  // боевой базе 30.07.2026: из 15 738 own-счетов «out» дата оплаты стоит у 12 193;
  // со стадией зеркала это расходится в обе стороны (1 172 «оплачен» без даты и
  // 2 014 дат на стадии «На проверку»/«Ждем оплату») — история как есть, не чиним.
  //
  // ЗЕРКАЛО: ПО КОДУ СТАДИИ, а не по семантике Bitrix, и коды берутся из
  // lib/dashboard.ts, где к ним написано, почему так (там же их использует кабинет
  // руководителя — иначе два экрана считали бы «оплачено» по-разному).
  //
  // Коротко причина: у типа 31 семантика НЕ расставлена — на живом ядре 26.07.2026 все восемь
  // стадий помечены `semantics='P'` («в работе»), включая `DT31_5:P` «Оплачен». Пока здесь
  // стояло `itemStageKind(...) === 'work'`, открытыми выглядели ВСЕ счета: «оплаченных» всегда
  // ноль, а в сумму «Счета открыты» входили оплаченные. Разница того же порядка, что в кабинете
  // руководителя: 1011 просроченных счетов против 136 действительно неоплаченных.
  //
  // Полные `stageId` собираются из справочника `/refs` (та же `stageIdsByCode`, что в
  // Dashboard): «DT31_5:P» зашивать нельзя — номер воронки в нём меняется вместе с настройками
  // Bitrix, а код стадии — нет.
  const invStagesKnown = refs.hasItemStages(INVOICE_TYPE)
  const invPaidStages = useMemo(
    () => stageIdsByCode(refs.itemStageOptions(INVOICE_TYPE), INVOICE_PAID_CODES), [refs],
  )
  // Пустой список — это «оплаченность определить нечем»: справочник стадий не пришёл либо
  // стадии с таким кодом в Bitrix больше нет. Тогда открытыми считаем ВСЕ счета страницы и
  // говорим об этом на экране: показать «оплаченных 0» как факт было бы тем же самым враньём,
  // от которого мы здесь и уходим.
  const invPaidKnown = invPaidStages.length > 0
  const invOpen = useMemo(() => {
    // Своя схема: открыт — значит даты оплаты в карточке нет. Пустая строка и
    // отсутствие поля здесь одно состояние (см. записьИзДокумента в api/smartOwn).
    if (счетаИзOwn) return invRows.filter((r) => !r[ДАТА_ОПЛАТЫ_СЧЁТА])
    if (!invPaidKnown) return invRows
    const paid = new Set(invPaidStages)
    return invRows.filter((r) => !paid.has(String(r.stageId ?? '')))
  }, [счетаИзOwn, invRows, invPaidStages, invPaidKnown])
  const invPaid = invRows.length - invOpen.length
  // Сумма — через moneyTotals: он же считает итоги в разделе смарт-процессов и не смешивает
  // валюты (складывать «515000|RUB» и «6000|EUR» в одно число нельзя).
  const oppCol = invColumns.byCode.get('opportunity')
  const invOpenSums = useMemo(
    () => moneyTotals(oppCol ? [oppCol] : [], invOpen),
    [oppCol, invOpen],
  )

  // ─── ИНН и банк по контрагенту ───
  // ИНН лежит в самой карточке компании — своего запроса за ним не идёт. Разбираем его
  // здесь, потому что от исхода зависит, спрашивать ли банк вообще: по тексту
  // «Иностранный агент» искать платежи нечем, и запрос был бы отправлен впустую.
  const инн = useMemo(() => разобратьИНН(company.data?.[ИНН_КОМПАНИИ]), [company.data])
  const права = useRightsApi()
  /**
   * Банк спрашиваем ТОЛЬКО когда есть чем спрашивать и кому смотреть.
   *
   * Права проверяются здесь, а не только при отрисовке: раздел «Банк и бухгалтерия» открыт
   * группе «бухгалтерия», и у продавца запрос вернул бы 403. Отказ в журнале доступа BFF на
   * каждое открытие карточки клиента — это шум, в котором потом не найти настоящую попытку
   * залезть не в свои деньги.
   */
  const банкМожно = инн.вид === 'инн' && !права.denied('bank')

  const c = company.data

  return (
    <QueryState isLoading={company.isLoading} error={company.error}>
      {c && (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="page-head" style={{ marginBottom: 10 }}>
              <h2 style={{ margin: 0 }}>{fmtText(c.TITLE)}</h2>
              <div className="spacer" />
              <span className="muted mono">#{c.ID}</span>
            </div>
            <dl className="dl">
              <dt>{f.title('COMPANY_TYPE')}</dt>
              <dd>{fmtText(f.enumText('COMPANY_TYPE', c.COMPANY_TYPE))}</dd>
              <dt>{f.title('INDUSTRY')}</dt>
              <dd>{fmtText(f.enumText('INDUSTRY', c.INDUSTRY))}</dd>
              <dt>{f.title('ASSIGNED_BY_ID')}</dt>
              <dd>{refs.userName(c.ASSIGNED_BY_ID)}</dd>
              <dt>Телефон</dt><dd>{fmtText(firstValue(c.PHONE))}</dd>
              <dt>Почта</dt><dd>{fmtText(firstValue(c.EMAIL))}</dd>
              <dt>В работе с</dt><dd>{fmtDate(c.DATE_CREATE)}</dd>
              {/* ИНН — не из реквизитов, а из обычного UF-поля карточки. Оно приезжает тем
                  же запросом, что и вся компания, поэтому строка ничего не стоит. Три
                  разных исхода разведены словами: см. lib/client360.разобратьИНН. */}
              <dt>{ИНН_ПОДПИСЬ}</dt>
              <dd>
                {инн.вид === 'нет'
                  ? <span className="muted">{инн.подпись}</span>
                  : (
                    <>
                      <span className={инн.вид === 'инн' ? 'mono' : ''}>{инн.значение}</span>
                      {' '}<span className="hint">({инн.подпись})</span>
                    </>
                  )}
              </dd>
            </dl>
            {!f.loaded && (
              <div className="hint" style={{ marginTop: 10 }}>
                Справочник полей недоступен — названия показаны кодами Bitrix.
              </div>
            )}
            {/* Раньше здесь стояло «ИНН ядро не отдаёт». Это было верно про РЕКВИЗИТЫ и
                неверно про ИНН: он лежит не в crm.requisite, а в UF-поле карточки, и
                оттуда его читает не только этот экран, но и проверка качества данных. */}
            <div className="hint" style={{ marginTop: 10 }}>
              ИНН взят из поля карточки Bitrix (в форме оно подписано «{ИНН_ПОДПИСЬ_В_BITRIX}»,
              своего названия в справочнике полей у него нет), а не из реквизитов:
              crm.requisite в ядро не залит, и банковских счетов, КПП и юридического адреса
              здесь по-прежнему нет. Поле свободного ввода: заполнено меньше чем у половины
              карточек, и не всегда цифрами — у части там текст, почти всегда «Иностранный
              агент». Последнее не ошибка, а ответ «российского ИНН нет».
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="page-head" style={{ marginBottom: 10 }}>
              <h2 style={{ margin: 0 }}>Коротко</h2>
              <div className="spacer" />
              <span className="hint">
                {дебиторкаСвоя
                  ? 'счётчики, долг и просрочка — точные, остальные суммы — по загруженным страницам'
                  : 'счётчики — точные, суммы — по загруженным страницам'}
              </span>
            </div>
            <div className="tiles">
              <div className="tile" title="Все сделки компании — счётчик ядра по всей выборке">
                <div className="t-label">Сделок всего</div>
                <div className="t-sum">{dealsAll.data?.stale ? '—' : dealsAll.data?.total ?? '—'}</div>
                <div className="t-label">за всё время</div>
              </div>
              <div className="tile" title="Сделки с CLOSED=N — счётчик ядра по всей выборке">
                <div className="t-label">Открытых</div>
                <div className="t-sum">{dealsOpen.data?.stale ? '—' : dealsOpen.data?.total ?? '—'}</div>
                <div className="t-label">в работе</div>
              </div>
              {/* Денежные плитки на устаревшей выборке показывают прочерк, а не ноль.
                  Счётчики сделок выше делают так же (`stale ? '—'`), а сумма — опаснее
                  счётчика: «Долг 0 ₽» рядом с пометкой «данным верить нельзя» человек
                  прочитает как «клиент не должен» и не позвонит. Ноль здесь — не итог
                  устаревших данных, а их отсутствие. */}
              <div className="tile">
                <div className="t-label">Портфель</div>
                <div className="t-sum">{dealsOpen.data?.stale ? '—' : fmtMoney(portfolio)}</div>
                <div className="t-label">
                  {dealsOpen.data?.stale
                    ? 'данные устарели'
                    : `по ${openRows.length} из ${dealsOpen.data?.total ?? 0} открытых`}
                </div>
              </div>
              {/* Долг и просрочка — по выбранному источнику (общий переключатель CRM).
                  Своя схема считает их SQL-агрегатом ПО ВСЕЙ выборке клиента — это точные
                  суммы, и единица другая: непогашенный СЧЁТ, а не запись «Платежи».
                  Зеркало — как было: по загруженной странице записей 134. */}
              {дебиторкаСвоя ? (
                <>
                  <div className={`tile${(просрочкаOwn?.count ?? 0) > 0 ? ' hot' : ''}`}
                       title="Непогашенные счета своей схемы с прошедшим сроком — SQL-агрегат ядра по всей выборке">
                    <div className="t-label">Просрочено</div>
                    <div className="t-sum">{своднаяOwn ? fmtMoney(просрочкаOwn?.sum ?? 0) : '—'}</div>
                    <div className="t-label">
                      {своднаяOwn
                        ? pluralN(просрочкаOwn?.count ?? 0, 'счёт', 'счёта', 'счетов')
                        : 'ждём свою схему'}
                    </div>
                  </div>
                  <div className="tile"
                       title="Остаток всех непогашенных счетов клиента — SQL-агрегат ядра по всей выборке">
                    <div className="t-label">Долг</div>
                    <div className="t-sum">{своднаяOwn ? fmtMoney(своднаяOwn.total.sum) : '—'}</div>
                    <div className="t-label">
                      {своднаяOwn
                        ? pluralN(своднаяOwn.total.count,
                                  'непогашенный счёт', 'непогашенных счёта', 'непогашенных счетов')
                        : 'ждём свою схему'}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className={`tile${!payments.data?.stale && payTotals.buckets.overdue.count ? ' hot' : ''}`}>
                    <div className="t-label">Просрочено</div>
                    <div className="t-sum">
                      {payments.data?.stale ? '—' : fmtMoney(payTotals.buckets.overdue.sum)}
                    </div>
                    <div className="t-label">
                      {payments.data?.stale
                        ? 'данные устарели'
                        : pluralN(payTotals.buckets.overdue.count, 'платёж', 'платежа', 'платежей')}
                    </div>
                  </div>
                  <div className="tile">
                    <div className="t-label">Долг</div>
                    <div className="t-sum">
                      {payments.data?.stale ? '—' : fmtMoney(payTotals.total.sum)}
                    </div>
                    <div className="t-label">
                      {payments.data?.stale ? 'данные устарели' : `${payTotals.total.count} неоплаченных`}
                    </div>
                  </div>
                </>
              )}
              {/* На устаревших данных ядро отдаёт пустой список, и «0 шт. на странице»
                  здесь означало бы «счетов нет». Прочерк, как у денежных плиток выше. */}
              <div className="tile" title={счетаИзOwn
                ? 'Счета своей схемы без даты оплаты в карточке — по загруженной странице'
                : invPaidKnown
                  ? 'Счета смарт-процесса 31 не на стадии «Оплачен» — по загруженной странице'
                  : 'Стадию «Оплачен» определить нечем: здесь все счета страницы, включая оплаченные'}>
                <div className="t-label">Счета открыты</div>
                <div className="t-sum">
                  {invoices.data?.stale || !invOpenSums.length
                    ? '—'
                    : invOpenSums.map((t) => fmtMoney(t.sum, t.currency)).join(' · ')}
                </div>
                <div className="t-label">
                  {invoices.data?.stale
                    ? 'данные устарели'
                    : `${invOpen.length} шт. на странице${счетаИзOwn || invPaidKnown ? '' : ' (с оплаченными)'}`}
                </div>
              </div>
              {/* «Флот 0 · все со сделками» на устаревших данных — два утверждения, из которых
                  неверны оба: судов не ноль, и про сделки по ним мы сейчас не знаем ничего.
                  Флот собирается из сделок и контактов, поэтому устарело — если устарело
                  любое из двух. */}
              <div className="tile">
                <div className="t-label">Флот</div>
                <div className="t-sum">
                  {dealsAll.data?.stale || contacts.data?.stale ? '—' : fleet.length}
                </div>
                <div className="t-label">
                  {dealsAll.data?.stale || contacts.data?.stale
                    ? 'данные устарели'
                    : (untouched > 0 ? `${untouched} без сделок` : 'все со сделками')}
                </div>
              </div>
            </div>
            <div className="hint" style={{ marginTop: 10 }}>
              «Сделок всего» и «Открытых» ядро считает по всей выборке — это точные числа.
              {дебиторкаСвоя
                ? ' Долг и просрочка — тоже точные: их считает своя схема CRM одним SQL по всем'
                  + ' непогашенным счетам клиента, и единица там — счёт, а не запись «Платежи»'
                  + ' Bitrix (зеркальные числа — переключателем источника, ?src=mirror).'
                  + ` Портфель и суммы счетов посчитаны по загруженным страницам (по ${PAGE} записей):`
                : ` Портфель, долг и суммы счетов посчитаны по загруженным страницам (по ${PAGE} записей):`}
              {' '}агрегатов зеркало не отдаёт, а выкачивать всю историю клиента на клиент ради одной
              суммы нельзя. Если под плиткой написано «по N из M» и N меньше M — сумма неполная.
              {hasOtherCurrency(openRows) && ' Внимание: среди открытых сделок есть не рублёвые —'
                + ' портфель сложен без пересчёта курса, доверять ему нельзя.'}
            </div>
          </div>

          <div className="grid-2">
            <div>
              <div className="card">
                <div className="page-head" style={{ marginBottom: 10 }}>
                  <h2 style={{ margin: 0 }}>Последние сделки</h2>
                  <div className="spacer" />
                  <Link to={`/deals?company=${companyId}`}>все сделки →</Link>
                </div>
                {/* Выборка сделок сужается правами роли (раздел `deals` в rights.py):
                    без этой пометки «сделок за компанией нет» читается как «клиент новый»,
                    хотя на деле это «его сделки ведёт не ваш отдел». */}
                <QueryState isLoading={dealsAll.isLoading} error={dealsAll.error}
                            stale={dealsAll.data?.stale} ageMin={dealsAll.data?.ageMin}
                            scope={dealsAll.data?.scope}>
                  {dealRows.length === 0 ? (
                    <div className="empty"><div className="empty-title">Сделок за этой компанией нет</div></div>
                  ) : (
                    /* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). */
                    <div className="table-wrap cards">
                      <table>
                        <thead>
                          <tr>
                            <th className="nowrap">ID</th>
                            <th>Название</th>
                            <th className="nowrap">Стадия</th>
                            <th className="nowrap">Судно</th>
                            <th className="num">Сумма</th>
                            <th className="nowrap">Изменена</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dealRows.slice(0, RELATED).map((d) => (
                            <tr key={d.ID}>
                              <td className="mono nowrap" data-label="ID">
                                <EntityLink kind="deal" id={d.ID} title={d.ID} />
                              </td>
                              <td data-label="Название">
                                <EntityLink kind="deal" id={d.ID} title={fmtText(d.TITLE)} />
                              </td>
                              <td className="nowrap" data-label="Стадия">
                                <span className={`badge ${refs.stageKind(d.STAGE_ID)}`}>
                                  {refs.stageName(d.STAGE_ID)}
                                </span>
                              </td>
                              <td className="nowrap" data-label="Судно">
                                <EntityLink kind="vessel" id={dealVesselId(d)}
                                            title={vesselNameById.get(dealVesselId(d)) || null} />
                              </td>
                              <td className="num" data-label="Сумма">
                                {fmtMoney(d.OPPORTUNITY, d.CURRENCY_ID || 'RUB')}
                              </td>
                              <td className="nowrap muted" data-label="Изменена">{fmtDate(d.DATE_MODIFY, true)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {dealRows.length > 0 && (
                    <div className="hint" style={{ marginTop: 8 }}>
                      Показаны {Math.min(RELATED, dealRows.length)} последних по изменению
                      из {dealsAll.data?.total ?? dealRows.length}.
                    </div>
                  )}
                </QueryState>
              </div>

              <div className="card">
                <div className="page-head" style={{ marginBottom: 10 }}>
                  <h2 style={{ margin: 0 }}>Дебиторка</h2>
                  <div className="spacer" />
                  {/* Ссылка сохраняет источник: из own-карточки — в own-ветку раздела,
                      иначе человек увидел бы там другие числа без объяснения. */}
                  <Link to={`/receivables?company=${companyId}${дебиторкаСвоя ? '&src=own' : ''}`}>
                    вся дебиторка →
                  </Link>
                </div>
                {дебиторкаСвоя ? (
                  /* ─── своя схема: непогашенные счета (recv_own_api) ─── */
                  <QueryState isLoading={recvСводка.isLoading || recvСписок.isLoading}
                              error={recvСводка.error || recvСписок.error}
                              scope={recvСписок.data?._scope ?? recvСводка.data?._scope}>
                    {строкиOwn.length === 0 ? (
                      <div className="empty">
                        <div className="empty-title">Непогашенных счетов за компанией нет</div>
                        <div className="empty-hint">
                          Это ответ своей схемы CRM: счета минус платежи с датой оплаты.
                          Часть зеркальной дебиторки в свою схему не переносилась — зеркальную
                          ветку смотрят переключателем источника (?src=mirror).
                        </div>
                      </div>
                    ) : (
                      /* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). */
                      <div className="table-wrap cards">
                        <table>
                          <thead>
                            <tr>
                              <th className="nowrap">Счёт</th>
                              <th className="nowrap">Сделка</th>
                              <th className="nowrap">Срок</th>
                              <th className="num">Остаток</th>
                            </tr>
                          </thead>
                          <tbody>
                            {строкиOwn.slice(0, RELATED).map((r) => (
                              <tr key={r.id}>
                                <td className="nowrap" data-label="Счёт">
                                  {r.no ? `№ ${r.no}` : `#${r.id}`}
                                  {!r.linked && (
                                    <span className="badge" style={{ marginLeft: 6 }}
                                          title="У счёта нет ни одной платёжной записи 134 — дебиторка Bitrix этот долг не видела">
                                      без записи 134
                                    </span>
                                  )}
                                </td>
                                <td className="nowrap" data-label="Сделка">
                                  <EntityLink kind="deal" id={r.deal_bitrix_id} title={r.deal_title} />
                                </td>
                                <td className="nowrap" data-label="Срок">
                                  {r.due_at ? fmtDate(r.due_at) : 'без срока'}
                                  {r.overdue_days !== null && r.overdue_days > 0 && (
                                    <div className="hint">просрочен на {r.overdue_days} дн.</div>
                                  )}
                                </td>
                                <td className="num" data-label="Остаток">
                                  <b>{fmtMoney(r.remainder, r.currency)}</b>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {строкиOwn.length > 0 && (
                      <div className="hint" style={{ marginTop: 8 }}>
                        Строка — непогашенный исходящий счёт своей схемы: остаток равен сумме
                        счёта минус платежи с датой оплаты, платёж без даты долг не гасит.
                        Считает ядро тем же кодом, что раздел «Дебиторка» с источником
                        «своя схема», — своего расчёта здесь нет.
                        {(recvСписок.data?.total ?? 0) > RELATED
                          && ` В таблице первые ${Math.min(RELATED, строкиOwn.length)} по сроку`
                             + ` оплаты из ${recvСписок.data?.total}; долг на плитке выше —`
                             + ' по всей выборке, а не по этим строкам.'}
                        {(своднаяOwn?.notes.unlinked.count ?? 0) > 0
                          && ` Счетов без платёжной записи 134: ${своднаяOwn!.notes.unlinked.count}`
                             + ` на ${fmtMoney(своднаяOwn!.notes.unlinked.sum)} — дебиторка Bitrix`
                             + ' этих долгов не видела, в итог они включены.'}
                        {(своднаяOwn?.notes.covered_unmarked.count ?? 0) > 0
                          && ` Погашены платежами, но без даты оплаты на счёте: ${своднаяOwn!.notes.covered_unmarked.count} — долгом не считаются.`}
                      </div>
                    )}
                  </QueryState>
                ) : (
                  /* ─── зеркало 134 (?src=mirror) — как было ─── */
                  <QueryState isLoading={payments.isLoading} error={payments.error}
                              stale={payments.data?.stale} ageMin={payments.data?.ageMin}
                              scope={payments.data?.scope}>
                    <PaymentsTable rows={payRows.slice(0, RELATED)} compact
                                   stale={payments.data?.stale}
                                   emptyText="Неоплаченных платежей за компанией нет" />
                    {payRows.length > 0 && (
                      <div className="hint" style={{ marginTop: 8 }}>
                        {payRows.length > RELATED && `В таблице первые ${RELATED} по сроку оплаты. `}
                        {(payments.data?.total ?? 0) > payRows.length
                          ? `Долг — по первым ${payRows.length} ${plural(payRows.length, 'записи', 'записям', 'записям')}`
                            + ` из ${payments.data?.total}: агрегатов ядро не отдаёт.`
                          : `Долг — по всем ${payRows.length} ${plural(payRows.length, 'записи', 'записям', 'записям')},`
                            + ' которые ядро отобрало как неоплаченные.'}
                        {/* Ядро фильтрует по пустой дате оплаты и воронке, а «оплачен» в Bitrix
                            определяется ещё флагом и стадией — такие строки приходят, но в долг
                            не идут. Без этой фразы «12 платежей» и «долг за 4» выглядят ошибкой. */}
                        {payTotals.paid.count > 0
                          && ` Из них ${payTotals.paid.count} на деле помечены оплаченными (флагом или стадией) — в сумму долга они не входят.`}
                        {payTotals.dirty > 0
                          && ` С противоречивыми признаками оплаты: ${payTotals.dirty} — помечены «?».`}
                      </div>
                    )}
                  </QueryState>
                )}
              </div>
            </div>

            <div>
              <div className="card">
                <div className="page-head" style={{ marginBottom: 10 }}>
                  <h2 style={{ margin: 0 }}>Контакты</h2>
                  <div className="spacer" />
                  <span className="muted">{people.length}</span>
                </div>
                <QueryState isLoading={contacts.isLoading} error={contacts.error}
                            stale={contacts.data?.stale} ageMin={contacts.data?.ageMin}
                            scope={contacts.data?.scope}>
                  {people.length === 0 ? (
                    <div className="empty"><div className="empty-title">Контактов не привязано</div></div>
                  ) : (
                    <ul className="plain-list">
                      {people.slice(0, RELATED).map((p) => (
                        <li key={p.ID}>
                          <EntityLink kind="contact" id={p.ID} title={contactName(p)} />
                          <div className="hint">
                            {[String(p.POST ?? ''), firstValue(p.PHONE), firstValue(p.EMAIL)]
                              .filter(Boolean).join(' · ') || 'должность и связь не заполнены'}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                  {(contacts.data?.total ?? 0) > PAGE && (
                    <div className="hint" style={{ marginTop: 8 }}>
                      У клиента {pluralN(contacts.data?.total ?? 0, 'контакт', 'контакта', 'контактов')} — ядро отдало первые {PAGE}.
                    </div>
                  )}
                </QueryState>
              </div>

              <div className="card">
                <div className="page-head" style={{ marginBottom: 10 }}>
                  <h2 style={{ margin: 0 }}>Флот</h2>
                  <div className="spacer" />
                  <span className="muted">{fleet.length}</span>
                </div>
                <QueryState isLoading={vesselContacts.isLoading} error={vesselContacts.error}
                            stale={vesselContacts.data?.stale} ageMin={vesselContacts.data?.ageMin}
                            scope={vesselContacts.data?.scope}>
                  {fleet.length === 0 ? (
                    <div className="muted">
                      Судов не нашли: в сделках поле «Судно» не заполнено, и контактов-судов
                      за компанией не числится.
                    </div>
                  ) : (
                    <ul className="plain-list">
                      {fleet.slice(0, 20).map((v) => (
                        <li key={v.id}>
                          <EntityLink kind="vessel" id={v.id} title={v.name} />
                          <div className="hint">
                            {[v.imo && `IMO ${v.imo}`,
                              v.deals > 0 ? `в сделках: ${v.deals}` : 'сделок не было']
                              .filter(Boolean).join(' · ')}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                  {fleet.length > 20 && (
                    <div className="hint" style={{ marginTop: 8 }}>
                      …и ещё {fleet.length - 20}
                    </div>
                  )}
                  {/* Это тот самый смысл, ради которого раздел флота есть на портале 55:
                      «суда есть, а сделок по ним нет» — прямой повод позвонить клиенту. */}
                  <div className="hint" style={{ marginTop: 10 }}>
                    Флот собран из двух мест: суда из поля «Судно» просмотренных
                    {' '}{pluralN(dealRows.length, 'сделка', 'сделки', 'сделок')} (из {dealsAll.data?.total ?? 0}) и контакты-суда,
                    привязанные к компании. «Сделок не было» значит «не встретилось в
                    просмотренных сделках» — при длинной истории список неполный.
                  </div>
                </QueryState>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="page-head" style={{ marginBottom: 10 }}>
              <h2 style={{ margin: 0 }}>Счета клиенту</h2>
              <div className="spacer" />
              <span className="muted">
                {invoices.data && !invoices.data.stale ? `всего: ${invoices.data.total}` : ''}
              </span>
            </div>
            {/* Пока ждём справочник полей, запрос счетов ещё не отправлен: у выключенного
                запроса isLoading=false, и без этой добавки на секунду мигало бы «счетов нет». */}
            <QueryState isLoading={invoices.isLoading || invFieldsPending} error={invoices.error}
                        stale={invoices.data?.stale} ageMin={invoices.data?.ageMin}
                        scope={invoices.data?.scope}>
              {/* Таблица — общая с разделом смарт-процессов: правила показа стадии, полей
                  «пишет бизнес-процесс» и раскрытия записи должны совпадать везде. */}
              <SmartItemsTable typeId={INVOICE_TYPE} rows={invRows} cols={invCols}
                               stale={invoices.data?.stale}
                               emptyText="Счетов за клиентом нет" />

              <div className="hint" style={{ marginTop: 10 }}>
                Колонки подобраны по справочнику полей ядра (`item:31`), а не зашиты в экран:
                переименуют поле в Bitrix — переименуется и заголовок.
                {/* Куда ушёл запрос, решает api/smartOwn — оговорка его словами, не пересказом. */}
                {оговоркаСчетов && ` ${оговоркаСчетов}`}
                {/* Как судится «оплачен» — у двух источников по-разному, и молчать нельзя
                    в обе стороны: у своей схемы это дата оплаты (первичный факт), у зеркала —
                    код стадии, у которого автор мы, а не портал. */}
                {счетаИзOwn && (
                  <>
                    {' '}<b>Оплаченность — по дате оплаты из карточки счёта</b>
                    {invPaid > 0 && `; на странице оплаченных ${invPaid}`}. Стадий Bitrix
                    в своей схеме нет как данных: в колонке «Стадия» — состояние своей схемы,
                    а оплачен счёт или нет, говорит перенесённая дата оплаты (пустая дата —
                    счёт открыт). Со стадиями зеркала она сходится не всегда — это расхождение
                    самого Bitrix, зеркальные числа смотрят переключателем ?src=mirror.
                  </>
                )}
                {!счетаИзOwn && invPaidKnown && (
                  <>
                    {' '}<b>Оплаченным считается счёт на стадии с кодом</b>
                    {' '}{INVOICE_PAID_CODES.join(', ')} («Оплачен»)
                    {invPaid > 0 && `; на странице таких ${invPaid}`}. Это код стадии, а не её
                    смысл: у типа 31 семантика в Bitrix не расставлена — все восемь стадий
                    помечены «в работе», включая «Оплачен», поэтому по смыслу открытыми
                    выглядели бы все счета подряд. «Оплачен частично» здесь тоже открытый счёт.
                    Заведут девятую стадию «Оплачен банком» — её код придётся дописать
                    в lib/dashboard.ts; правильное лечение — расставить семантику в Bitrix,
                    тогда список кодов удаляется.
                  </>
                )}
                {!счетаИзOwn && !invPaidKnown && invStagesKnown
                  && ` Стадии с кодом ${INVOICE_PAID_CODES.join(', ')} в справочнике типа 31`
                   + ' сейчас нет, поэтому оплаченные счета отделить нечем: в плитке'
                   + ' «Счета открыты» и её сумме все счета страницы, включая оплаченные.'}
                {!счетаИзOwn && !invStagesKnown
                  && ' Справочник стадий типа 31 из ядра не пришёл: стадии показаны'
                  + ' сырыми кодами Bitrix, и оплаченные счета отделить нечем — в плитке'
                  + ' «Счета открыты» и её сумме все счета страницы, включая оплаченные.'}
                {!invFields.loaded && ' Справочник полей типа 31 недоступен — видны только'
                  + ' системные колонки, названия показаны кодами.'}
                {(invoices.data?.total ?? 0) > invRows.length
                  && ` Показана первая страница: ${invRows.length} из ${invoices.data?.total}, суммы — по ней же.`}
              </div>
            </QueryState>
          </div>

          {/* ─── блоки, которых здесь раньше не было ───
              Все пять появились 28.07.2026, когда у ручек ядра завелись параметры отбора по
              клиенту. До этого на их месте стоял честный список «чего здесь нет и почему» —
              он никуда не делся, но короче на пять пунктов.

              КАЖДЫЙ БЛОК — ОДИН ЗАПРОС И НИ ОДНОЙ ЦЕПОЧКИ. Ни один из них не ждёт ответа
              другого: все уходят одной волной вместе со сделками и счетами. Порог обращений
              на открытие экрана закреплён в tests/nplus1.test.tsx — он и стережёт это. */}
          <ГдеВсталаРабота companyId={companyId} название={fmtText(c.TITLE)} />
          <ЗаданияПоКлиенту companyId={companyId} название={fmtText(c.TITLE)} />
          <ДокументыИз1С companyId={companyId} название={fmtText(c.TITLE)} />
          <РасхожденияОтветственного ответственный={refId(c.ASSIGNED_BY_ID)}
                                     имя={refs.userName(c.ASSIGNED_BY_ID)} />
          <КачествоПоКлиенту companyId={companyId} название={fmtText(c.TITLE)} />

          {/* ─── деньги от клиента в банке ─── */}
          {/* Блок монтируется ТОЛЬКО когда есть чем спрашивать и кому смотреть: тот же приём,
              что у самого ClientBody. Иначе на каждое открытие карточки уходил бы запрос,
              который вернёт 403 продавцу и пустоту иностранному клиенту без ИНН. */}
          {банкМожно
            ? <БанкПоКонтрагенту инн={инн.значение} название={fmtText(c.TITLE)} />
            : <БанкНеСпрашиваем инн={инн} закрыт={права.denied('bank')} />}

          <div className="card">
            <h2>Чего на этом экране нет и почему</h2>
            <ul className="plain-list">
              <li className="muted">
                <b>Правки.</b> Ни одного поля отсюда изменить нельзя. Запись в сделку, платёж или
                счёт запускает бизнес-процессы Bitrix, которые раздают задания людям, — это
                отдельная задача с write-through в ядре, а не кнопка на карточке.
              </li>
              <li className="muted">
                <b>Итогов за всё время.</b> Ядро отдаёт страницами по {PAGE} и не умеет
                группировку, поэтому «сколько мы на клиенте заработали» посчитать нечем. Точны
                только счётчики. Появится агрегат в ядре — суммы станут полными без правок здесь.
              </li>
              <li className="muted">
                <b>RFM, ABC и охвата флота.</b> На портале 55 они есть
                (`clientFleet`), но считаются по внешнему снимку базы флота, а не по Bitrix.
                Пока такого источника у ядра нет, сегмент клиента мы бы выдумали.
              </li>
              <li className="muted">
                <b>Дел, звонков и ленты писем.</b> Зеркало активностей в ядре уже есть
                (bxh_activities, наполняет sync_bx_hist.py — письма, звонки, дела с 2025
                года), но ручки над ним ещё нет — «когда последний раз общались» здесь
                пока взять нечем. Накопленная лента переживёт отключение Bitrix как
                история: новых писем в ней после отключения не прибавится, сам синк
                читает Bitrix REST.
              </li>
              {/* Ниже — не «не успели», а «ручке ядра нечем задать вопрос про одного
                  клиента». Каждый пункт называет, чего именно не хватает: так владельцу
                  видно цену, а следующему разработчику — куда идти.

                  Пять пунктов отсюда 28.07.2026 УБРАНЫ и стали блоками выше: у ручек
                  появились параметры `company`, `deal_bx`, `imo` и `assigned`. Список стоит
                  держать в этом состоянии — пункт живёт ровно столько, сколько живёт
                  причина, иначе экран будет отказывать в том, что уже умеет. */}
              <li className="muted">
                <b>Свода качества данных по клиенту.</b> Строки проверок сузить можно и они
                показаны выше, а вот сам свод «сколько находок какой степени» — нельзя:
                на <code>/dq/checks?company=…</code> ядро отвечает 400 нарочно. Свод считается
                агрегатами по всей базе, и урезанный по клиенту он отвечал бы на другой вопрос
                молча. Поэтому выше не «сводка по клиенту», а три названные проверки.
              </li>
              <li className="muted">
                <b>Сводки заданий процессов по людям.</b> Сами задания по клиенту показаны
                выше, но разбивка «кто сколько держит» (`/bp/by-user`) на `?company=` отвечает
                400 — по той же причине: это агрегат по всем живым заданиям сразу.
              </li>
              <li className="muted">
                <b>Расхождений состава ПО КЛИЕНТУ.</b> У `/products/mismatches` есть отбор по
                ответственному, но не по компании — блок выше поэтому про человека, а не про
                клиента, и подписан именно так. Сводка в том ответе вообще не сужается ничем:
                это сказано словами ядра прямо в блоке.
              </li>
            </ul>
          </div>
        </>
      )}
    </QueryState>
  )
}

// ─────────────────────── деньги от клиента в банке ───────────────────────

/**
 * Приходы от контрагента, которые бухгалтерия ещё не разнесла.
 *
 * ЗАЧЕМ ОН НА КАРТОЧКЕ КЛИЕНТА. Дебиторка выше отвечает «сколько он должен по Bitrix».
 * Неразнесённый приход — это ответ на встречный вопрос: «а он, может быть, уже заплатил, и
 * долг ненастоящий?». Сегодня на этот вопрос ходят в раздел «Банк» и ищут по названию
 * руками. Замер 28.07.2026 по боевому зеркалу (`SELECT` по `pbh_bank_rows` и `companies`):
 * строк выписки со статусом «не разнесено» — 1 381, и по ИНН они сходятся с 378 разными
 * ИНН из карточек компаний. То есть повод позвонить есть не у одного клиента, а у сотен.
 *
 * СВОЕГО РАСЧЁТА НЕТ. И отбор «не разнесено» (`status='none'`), и итоги считает `pbh_api`
 * тем же кодом, что раздел «Банк и бухгалтерия». Здесь только другой фильтр — по ИНН.
 *
 * СВЯЗЬ УГАДАНА НЕ БУДЕТ. Ищем по точному ИНН, а не по названию: «ООО Победит» в назначении
 * платежа и «ООО "Победит"» в Bitrix — это не совпадение строк, и подставлять сюда поиск по
 * подстроке значило бы приписывать клиенту чужие деньги. Об этом сказано на экране.
 */
function БанкПоКонтрагенту({ инн, название }: { инн: string; название: string }) {
  const банк = useВыписка(useMemo(
    () => ({ inn: инн, unlinked: true, dir: 'in', limit: RELATED }), [инн],
  ))
  const строки = банк.data?.rows ?? []
  const t = банк.data?.totals

  return (
    <div className="card">
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Деньги от клиента в банке</h2>
        <div className="spacer" />
        <Link to="/bank">раздел «Банк и бухгалтерия» →</Link>
      </div>

      <QueryState isLoading={банк.isLoading} error={банк.error}>
        {/* Граница данных — НАД цифрами, тем же приёмом, что в «Сквозных отчётах»: человек
            читает число первым, и к этому моменту должен знать, за какие дни оно посчитано.
            Даты — границы ИМЕННО ЭТИХ строк, а не всей выписки. */}
        <div className="notice" style={{ marginBottom: 10 }}>
          {t?.first_day && t?.last_day
            ? <>Найденные строки — с <b>{fmtDate(t.first_day)}</b> по <b>{fmtDate(t.last_day)}</b>.
                За пределами этих дней «приходов не было» означает «в выписке за те дни таких
                строк нет», а не «клиент не платил».</>
            : <>Неразнесённых приходов от этого ИНН в выписке нет, поэтому и границы дат нет.
                Полная выписка по контрагенту — в разделе «Банк и бухгалтерия».</>}
        </div>

        <div className="tiles">
          <div className={`tile${(t?.unlinked_n ?? 0) > 0 ? ' hot' : ''}`}
               title="Приходы от этого ИНН со статусом «не разнесено» — итог ядра по всей выборке">
            <div className="t-label">Не разнесено приходов</div>
            <div className="t-sum">{t?.unlinked_n ?? 0}</div>
            <div className="t-label">
              {(t?.unlinked_n ?? 0) > 0 ? 'бухгалтерия ещё не привязала' : 'все приходы разнесены'}
            </div>
          </div>
          <div className="tile">
            <div className="t-label">На сумму</div>
            <div className="t-sum">{fmtMoney(t?.unlinked_rub ?? 0)}</div>
            <div className="t-label">точная сумма, не по странице</div>
          </div>
          {/* Третьей плитки «всего платежей от клиента» здесь нет намеренно: запрос ушёл
              с фильтром «приход и не разнесено», и `totals` посчитаны по этой же выборке.
              Показать их как итог по контрагенту значило бы выдать часть за целое —
              ошибку того же рода, от которой на этом экране везде стоит «по N из M». */}
        </div>

        {строки.length === 0 ? (
          <div className="empty" style={{ marginTop: 12 }}>
            <div className="empty-title">
              Неразнесённых приходов от {название} в выписке нет
            </div>
            <div className="empty-hint">
              Искали по точному ИНН {инн}. Это не «денег от него не приходило»: разнесённые
              платежи сюда не попадают, а приход, в котором банк написал другой ИНН, не найдётся
              вовсе. Полная выписка — в разделе «Банк и бухгалтерия».
            </div>
          </div>
        ) : (
          /* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). */
          <div className="table-wrap cards" style={{ marginTop: 12 }}>
            <table>
              <thead>
                <tr>
                  <th className="nowrap">Дата</th>
                  <th className="num">Сумма</th>
                  <th>Назначение платежа</th>
                  <th className="nowrap">Почему не разнесено</th>
                </tr>
              </thead>
              <tbody>
                {строки.map((r) => (
                  <tr key={r.src_id}>
                    <td className="nowrap" data-label="Дата">{fmtDate(r.op_date)}</td>
                    <td className="num" data-label="Сумма">{fmtMoney(r.amount)}</td>
                    <td data-label="Назначение платежа">
                      {fmtText(r.purpose)}
                      {r.refs && <div className="hint">в назначении есть номер: {r.refs}</div>}
                    </td>
                    <td className="nowrap" data-label="Почему не разнесено">
                      <span className="hint">{fmtText(r.why)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="hint" style={{ marginTop: 10 }}>
          Отбор — <b>по точному ИНН {инн}</b> из карточки Bitrix, а не по названию: совпадение
          названий в назначении платежа и в CRM — не доказательство, и приписывать клиенту
          чужой платёж этот экран не станет. «Не разнесено» и суммы считает ядро тем же кодом,
          что раздел «Банк и бухгалтерия», — своего расчёта здесь нет.
          {(банк.data?.total ?? 0) > строки.length
            && ` Показаны первые ${строки.length} из ${банк.data?.total}; числа на плитках —`
               + ' по всей выборке, а не по этой странице.'}
          {' '}Обе плитки считают ТОЛЬКО неразнесённые приходы: запрос ушёл с этим фильтром,
          и «сколько всего клиент заплатил» здесь не написано, потому что этого не спрашивали.
          {' '}Раздел «Банк» видит только бухгалтерия — эта карточка показывает его тем же,
          кому он открыт, и никому больше.
        </div>
      </QueryState>
    </div>
  )
}

/**
 * Почему банк не спрошен. Отдельный блок, а не пустое место: молчание здесь читалось бы как
 * «денег от него не приходило», а это три разных случая, и ни один из них не «не приходило».
 */
function БанкНеСпрашиваем({ инн, закрыт }: { инн: ЧтоСИНН; закрыт: boolean }) {
  return (
    <div className="card">
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Деньги от клиента в банке</h2>
      </div>
      <div className="empty">
        <div className="empty-title">
          {закрыт
            ? 'Раздел «Банк и бухгалтерия» вашей роли недоступен'
            : инн.вид === 'нет'
              ? 'Искать платежи нечем: ИНН в карточке не заполнен'
              : 'Искать платежи нечем: в поле ИНН не номер, а текст'}
        </div>
        <div className="empty-hint">
          {закрыт
            ? 'Это отказ по правам, а не «платежей нет»: строки выписки существуют, но '
              + 'банковские данные открыты только бухгалтерии. Запрос за ними отсюда даже '
              + 'не отправлялся.'
            : инн.вид === 'нет'
              ? 'Банковская выписка сводится с контрагентом по ИНН и только по нему. '
                + 'Заполнят поле в Bitrix — блок появится сам, отдельной работы для этого '
                + 'не нужно.'
              : `В поле стоит «${инн.значение}». Для «Иностранного агента» это правильный `
                + 'ответ: российского ИНН у клиента нет, и его платежи в рублёвой выписке '
                + 'не ищутся по определению.'}
        </div>
      </div>
    </div>
  )
}

// ══════════════════ где встала работа по клиенту (`/chain/gaps?company=`) ══════════════════

/**
 * Разрывы цепочки по сделкам ЭТОГО клиента.
 *
 * ПОЧЕМУ ДВЕРЬ СТОИТ НА КЛИЕНТЕ, А НЕ ТОЛЬКО НА СЕРВЕРЕ. Параметр `company` у ручки есть,
 * а у ДВЕРИ его нет: BFF зовёт `rights.проверить_чтение_свода` (web/bff/main.py, ветка
 * `/api/chain/gaps`), и она смотрит только на зону — «видна ли человеку вся база». Роль
 * «руководитель» и «сотрудник» получат 403 независимо от того, про одного клиента спросили
 * или про всю базу. Поэтому запрос отсюда НЕ УХОДИТ ВОВСЕ, а на месте блока стоит
 * объяснение: тот же приём и та же формулировка, что в pages/Mismatches.tsx.
 *
 * Пустой блок здесь читался бы как «по клиенту ничего не встало» — то есть как «всё в
 * порядке». Это худшая неправда, какую способен сказать блок, ради которого он заведён.
 *
 * ЗАЧЕМ ЭТО ВООБЩЕ НА КАРТОЧКЕ КЛИЕНТА. Отбор настоящий и дешёвый: замер боевого ядра
 * 28.07.2026 — по всей базе 1 445 сделок с разрывом за 0,76 с, по клиенту 3262 их 32
 * за 0,06 с. Перебирать сделки клиента по одной (прежняя причина, по которой блока здесь
 * не было) больше не нужно: ядро отбирает их одним SQL.
 */
function ГдеВсталаРабота({ companyId, название }: { companyId: string; название: string }) {
  const права = useRightsApi()
  if (права.loaded && (права.denied('deals') || права.scoped('deals'))) {
    return <РазрывыНеОткрыты свой={права.scoped('deals')} основание={права.basis} />
  }
  return <РазрывыКлиента companyId={companyId} название={название} />
}

function РазрывыНеОткрыты({ свой, основание }: { свой: boolean; основание: string }) {
  return (
    <div className="card">
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Где встала работа по клиенту</h2>
      </div>
      <div className="empty">
        <div className="empty-title">Этот свод вам не открыт</div>
        <div className="empty-hint">
          {свой
            ? 'Разрывы цепочки открыты тем, кому видна вся база: дверь смотрит на зону, а не'
              + ' на то, про кого спрашивают. Отбор по клиенту у ручки есть, но роль со своей'
              + ' зоной получит отказ и с ним.'
            : 'Раздел сделок закрыт вашей роли, а разрывы цепочки — это деньги сделок.'}
          {основание ? ` Ваша зона: ${основание}.` : ''}
          {' '}Запрос отсюда не отправлялся вовсе. Пусто на этом месте означало бы «по клиенту
          ничего не встало», и показывать пустоту вместо отказа мы не станем.
        </div>
      </div>
    </div>
  )
}

/** Сам блок. Рисуется только тогда, когда запрос имеет право уйти. */
function РазрывыКлиента({ companyId, название }: { companyId: string; название: string }) {
  const разрывы = useРазрывы(useMemo(() => ({ company: companyId, limit: PAGE }), [companyId]))
  const строки = useMemo(() => разрывы.data?.rows ?? [], [разрывы.data])
  const поВидам = useMemo(() => разрывыПоВидам(строки), [строки])
  const всего = разрывы.data?.total ?? 0
  /** Страница вместила всю выборку — значит числа по видам точные, а не «не меньше чем». */
  const полно = строки.length >= всего
  const виды = useMemo(
    () => (разрывы.data?.kinds ?? []).filter((к) => (поВидам.get(к.code) ?? 0) > 0),
    [разрывы.data, поВидам],
  )

  return (
    <div className="card">
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Где встала работа по клиенту</h2>
        <div className="spacer" />
        <Link to="/chain">раздел «Путь сделки» →</Link>
      </div>

      {/* Граница данных — НАД цифрами: разрыв «счёта нет» у сделки 2019 года не значит
          ничего, потому что счета в зеркале ведут с 25.04.2023. Окно и пороги приезжают
          с ответом, а не зашиты в экран. */}
      <div className="notice" style={{ marginBottom: 10 }}>
        Окно — <b>{fmtDays(разрывы.data?.window?.days ?? 90)}</b> по дате создания сделки
        {разрывы.data?.window?.today && `, на ${fmtDate(разрывы.data.window.today)}`}. За его
        пределами блок молчит намеренно: у каждого источника своё начало, и «записи нет» там
        означает «источник про те дни не знает», а не остановку работы.
      </div>

      <QueryState isLoading={разрывы.isLoading} error={разрывы.error}>
        <div className="tiles">
          <div className={`tile${всего > 0 ? ' hot' : ''}`}
               title="Сделки клиента, у которых порвано хоть одно звено — счётчик ядра по всей выборке">
            <div className="t-label">Сделок с разрывом</div>
            <div className="t-sum">{всего}</div>
            <div className="t-label">у этого клиента</div>
          </div>
          {/* Названия видов берутся ИЗ ОТВЕТА. Не пришли — плиток нет вовсе: печатать код
              `no_invoice` вместо заголовка значило бы показать внутреннее имя, а завести
              русские подписи здесь — развести вторую пару названий рядом с теми, что ядро
              уже пишет в «Пути сделки». */}
          {виды.map((вид) => {
            const n = поВидам.get(вид.code) ?? 0
            const деньги = разрывыРубли(строки, вид.code)
            return (
              <div className="tile hot" key={вид.code} title={вид.why}>
                <div className="t-label">{вид.title}</div>
                <div className="t-sum">{n}</div>
                <div className="t-label">на {fmtMoney(деньги.sum)}</div>
              </div>
            )
          })}
        </div>

        {строки.length === 0 ? (
          <div className="empty" style={{ marginTop: 12 }}>
            <div className="empty-title">
              По сделкам {название} в этом окне цепочка не рвётся
            </div>
            <div className="empty-hint">
              Проверено {fmtDays(разрывы.data?.window?.days ?? 90)}: счёт после принятой работы,
              карточка платежа после счёта, срок оплаты, состав против суммы. Это не «сделок
              нет» — это «в перечисленных местах ничего не встало».
            </div>
          </div>
        ) : (
          /* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). */
          <div className="table-wrap cards" style={{ marginTop: 12 }}>
            <table>
              <thead>
                <tr>
                  <th>Сделка</th>
                  <th className="nowrap">Где встало</th>
                  <th>Что ещё порвано</th>
                  <th className="num">Сумма</th>
                </tr>
              </thead>
              <tbody>
                {строки.slice(0, RELATED).map((r) => (
                  <tr key={r.deal_id}>
                    <td data-label="Сделка">
                      <EntityLink kind="deal" id={r.deal_id} title={r.title || `#${r.deal_id}`} />
                    </td>
                    <td className="nowrap" data-label="Где встало">
                      {r.stuck_at
                        ? <span className="badge lose">{r.stuck_at.title}</span>
                        : <span className="muted">только наблюдения</span>}
                    </td>
                    <td data-label="Что ещё порвано">
                      <span className="hint">
                        {(r.gaps ?? [])
                          .filter((к) => к !== r.stuck_at?.code)
                          .map((к) => виды.find((в) => в.code === к)?.title ?? к)
                          .join(' · ') || '—'}
                      </span>
                    </td>
                    <td className="num" data-label="Сумма">
                      {fmtMoney(r.opportunity, r.currency || 'RUB')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="hint" style={{ marginTop: 8 }}>
          <b>Сделок с разрывом — {всего}</b>: это счётчик ядра по суженной выборке, он точный.
          Числа на остальных плитках посчитаны по {строки.length}
          {' '}{plural(строки.length, 'загруженной строке', 'загруженным строкам',
                      'загруженным строкам')}
          {полно
            ? ' — то есть по всей выборке целиком, потому что она в страницу поместилась.'
            : ` из ${всего}: страница ядра ${PAGE} строк, и по видам это «не меньше чем».`}
          {' '}Что считать разрывом, решает ядро (`chain_api`) — тем же кодом, что раздел
          «Путь сделки». Ссылка выше ведёт туда БЕЗ отбора по клиенту: раздел держит фильтры
          в своём состоянии, а не в адресе.
          {' '}Блок открыт только тем, кому видна вся база: дверь BFF отбора по клиенту не
          видит, и роли со своей зоной он не показывается вовсе.
        </div>

        {разрывы.data?.data_notes && (
          <ul className="hint" style={{ marginTop: 8, paddingLeft: 18 }}>
            {Object.entries(разрывы.data.data_notes).map(([к, т]) => <li key={к}>{т}</li>)}
          </ul>
        )}
      </QueryState>
    </div>
  )
}

// ═══════════════ задания процессов по клиенту (`/bp/tasks?company=`) ═══════════════

/**
 * Что висит по сделкам этого клиента в бизнес-процессах Bitrix.
 *
 * ЭТО ТРЕТЬЕ ИЗМЕРЕНИЕ, и оно не сводится ни с деньгами, ни с разрывами цепочки. Задание
 * адресовано КОНКРЕТНОМУ человеку (`bp_tasks.user_id`), а не ответственному за сделку:
 * согласование уходит руководителю, которого в самой сделке нет. Складывать «горящие сделки»
 * и «висящие задания» нельзя, и на экране они поэтому в разных блоках.
 *
 * ЧИСЛО ЗДЕСЬ ТОЧНОЕ, в отличие от денежных плиток выше: `total` — счётчик ядра по всей
 * суженной выборке, а не по загруженной странице.
 *
 * Дверь ролевая: раздел 'bp-tasks' в web/bff/rights.py. Роли без него ручка ответит 403 —
 * отсюда запрос не уходит вовсе. Сузить по клиенту можно только САМИ задания: сводка по
 * людям (`/bp/by-user`) — агрегат по всем живым заданиям сразу, и на `?company=` ядро
 * отвечает 400 нарочно. Такого вызова здесь нет.
 */
function ЗаданияПоКлиенту({ companyId, название }: { companyId: string; название: string }) {
  const права = useRightsApi()
  const закрыт = права.loaded && права.denied('bp-tasks')
  const задания = useЗадания(
    useMemo(() => ({ company: companyId, limit: RELATED }), [companyId]), !закрыт,
  )
  const строки = задания.data?.rows ?? []
  const всего = задания.data?.total ?? 0
  /** Задания на отключённых людях: их не закроет никто, и это главная находка блока. */
  const уволенные = строки.filter((r) => r.user_active === false).length

  if (закрыт) {
    return (
      <div className="card">
        <div className="page-head" style={{ marginBottom: 10 }}>
          <h2 style={{ margin: 0 }}>Задания процессов по клиенту</h2>
        </div>
        <div className="empty">
          <div className="empty-title">Раздел «Задания процессов» вашей роли недоступен</div>
          <div className="empty-hint">
            Это отказ по правам, а не «заданий нет»: процессы по сделкам клиента идут, но
            видеть их этой роли не разрешено. Запрос отсюда не отправлялся.
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="card">
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Задания процессов по клиенту</h2>
        <div className="spacer" />
        <Link to="/bp-tasks">раздел «Задания процессов» →</Link>
      </div>

      <QueryState isLoading={задания.isLoading} error={задания.error}>
        <div className="tiles">
          <div className={`tile${всего > 0 ? ' hot' : ''}`}
               title="Живые задания по сделкам клиента — счётчик ядра по всей суженной выборке">
            <div className="t-label">Живых заданий</div>
            <div className="t-sum">{всего}</div>
            <div className="t-label">точное число, не по странице</div>
          </div>
          <div className={`tile${уволенные > 0 ? ' hot' : ''}`}
               title="Задание ждёт человека, которого отключили — закрыть его некому">
            <div className="t-label">На отключённых</div>
            <div className="t-sum">{уволенные}</div>
            <div className="t-label">
              {уволенные > 0 ? 'их не закроет никто' : 'по загруженной странице'}
            </div>
          </div>
        </div>

        {строки.length === 0 ? (
          <div className="empty" style={{ marginTop: 12 }}>
            <div className="empty-title">
              По сделкам {название} живых заданий процессов нет
            </div>
            <div className="empty-hint">
              Это «никого не ждут», а не «процессы не запускались»: закрытые задания в зеркале
              bizproc неполны, и блок показывает только живые. История процессов — в разделе
              «Задания процессов».
            </div>
          </div>
        ) : (
          /* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). */
          <div className="table-wrap cards" style={{ marginTop: 12 }}>
            <table>
              <thead>
                <tr>
                  <th>Что сделать</th>
                  <th className="nowrap">Кого ждёт</th>
                  <th className="nowrap">Сделка</th>
                  <th className="num">Ждёт, дней</th>
                </tr>
              </thead>
              <tbody>
                {строки.map((r) => (
                  <tr key={r.row_key}>
                    <td data-label="Что сделать">
                      {fmtText(r.name)}
                      {r.template_name && <div className="hint">{r.template_name}</div>}
                    </td>
                    <td className="nowrap" data-label="Кого ждёт">
                      {fmtText(r.user_name)}
                      {r.user_active === false && (
                        <div className="hint">учётка отключена — закрыть некому</div>
                      )}
                    </td>
                    <td className="nowrap" data-label="Сделка">
                      <EntityLink kind="deal" id={r.deal_id}
                                  title={r.deal_id ? `#${r.deal_id}` : null} />
                    </td>
                    <td className="num" data-label="Ждёт, дней">
                      {r.days_waiting ?? <span className="muted">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="hint" style={{ marginTop: 8 }}>
          <b>Живых заданий — {всего}</b>: счётчик ядра по всем заданиям, привязанным к сделкам
          этого клиента. В таблице {pluralN(строки.length, 'самое старое', 'самых старых',
            'самых старых')} — ядро сортирует по возрасту, начинать надо с них.
          {' '}Задание адресовано конкретному человеку, а не ответственному за сделку:
          согласование уходит руководителю, которого в сделке нет. С «горящими сделками» выше
          эти числа не складываются.
          {' '}Разбивки «кто сколько держит» здесь нет и не будет: `/bp/by-user` — агрегат по
          всем живым заданиям сразу, и сузить его до клиента нечем (ядро отвечает 400).
          {задания.data?.note && ` ${задания.data.note}`}
        </div>
      </QueryState>
    </div>
  )
}

// ═════════════ документы 1С по клиенту (`/ut/docs?company=`) ═════════════

/** Виды документов 1С словами. Ядро отдаёт код вида; своих правил про данные здесь нет. */
const ВИД_ДОКУМЕНТА: Record<string, string> = {
  sale: 'Реализация',
  order: 'Заказ клиента',
  supply: 'Заказ поставщику',
}

/**
 * Реализации, заказы клиента и заказы поставщику из зеркала 1С.
 *
 * СВЯЗЬ КОСВЕННАЯ — и это главное, что блок обязан сказать вслух. Прямой ссылки «компания
 * Bitrix ↔ контрагент 1С» в зеркале НЕТ ВОВСЕ. Цепочка идёт через сделку: номер сделки
 * Bitrix лежит в Description справочника сделок 1С и разобран при заливке. Значит:
 *
 *   • документ, оформленный без сделки, к клиенту не привяжется НИКОГДА — таких
 *     652 из 2 984 на 28.07.2026, и в этот ответ они не попадают ни при каком отборе;
 *   • «документов нет» здесь означает «нет под сделками этого клиента», а НЕ «отгрузки
 *     не было». Без этой фразы блок соврал бы этими 652 документами (на 28.07.2026).
 *
 * КОНТРАГЕНТОВ У ОДНОГО КЛИЕНТА БЫВАЕТ МНОГО. У 3262 их тринадцать (замер 28.07.2026:
 * 91 документ, 13 разных `contragent_ref`). Это законно — юрлицо завели в 1С дважды, — но
 * спросить 1С по одной ссылке из списка значит получить часть. Поэтому показан весь список,
 * а не «главный контрагент».
 *
 * НАЗВАНИЙ КОНТРАГЕНТОВ ЗДЕСЬ НЕТ: в зеркале документов лежат только ссылки 1С, названия
 * живут в справочниках за прокси. Тянуть их отсюда значило бы завести по запросу на строку
 * ради подписи — ровно то, от чего этот экран бережётся везде.
 */
function ДокументыИз1С({ companyId, название }: { companyId: string; название: string }) {
  const права = useRightsApi()
  const закрыт = права.loaded && права.denied('shipping')
  const доки = useДокументыКлиента(
    useMemo(() => ({ company: companyId, limit: RELATED }), [companyId]), !закрыт,
  )
  const строки = доки.data?.rows ?? []
  const всего = доки.data?.total ?? 0
  const виды = доки.data?.totals?.by_kind ?? []
  const контрагенты = доки.data?.contragents ?? []
  const покрытие = доки.data?.coverage
  const граница = доки.data?.mirror_scope

  if (закрыт) {
    return (
      <div className="card">
        <div className="page-head" style={{ marginBottom: 10 }}>
          <h2 style={{ margin: 0 }}>Документы 1С по клиенту</h2>
        </div>
        <div className="empty">
          <div className="empty-title">Раздел «Отгрузка (1С УТ)» вашей роли недоступен</div>
          <div className="empty-hint">
            Это отказ по правам, а не «документов нет»: реализации и заказы по сделкам клиента
            в зеркале есть. Запрос отсюда не отправлялся.
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="card">
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Документы 1С по клиенту</h2>
        <div className="spacer" />
        <Link to="/shipping">раздел «Отгрузка (1С УТ)» →</Link>
      </div>

      {/* Граница данных — НАД цифрами: без неё «документов нет» читается как «отгрузки
          не было», а это разные вещи. Числа — из ответа ядра, не из этого файла. */}
      <div className="notice" style={{ marginBottom: 10 }}>
        Связь с клиентом <b>косвенная</b>: прямой ссылки «компания Bitrix → контрагент 1С»
        в зеркале нет вовсе, документы найдены ЧЕРЕЗ СДЕЛКИ — номер сделки Bitrix записан
        в справочнике сделок 1С.
        {граница && <>
          {' '}Поэтому {граница.docs_no_deal} из {граница.docs_total}
          {' '}{plural(граница.docs_no_deal, 'документа зеркала', 'документов зеркала',
                      'документов зеркала')} не привязаны ни к какой компании и в этот блок
          не попадут ни при каком отборе.
        </>}
        {' '}«Документов нет» здесь означает «нет под сделками этого клиента», а не
        «отгрузки не было».
      </div>

      <QueryState isLoading={доки.isLoading} error={доки.error}>
        <div className="tiles">
          <div className="tile" title="Документы 1С под сделками клиента — счётчик ядра по всей выборке">
            <div className="t-label">Документов</div>
            <div className="t-sum">{всего}</div>
            <div className="t-label">точное число, не по странице</div>
          </div>
          {/* Итоги по видам считает ядро ОДНИМ SQL по всей выборке, а не по странице:
              это точные суммы, и они подписаны именно так. */}
          {виды.map((в) => (
            <div className="tile" key={в.kind} title={`оформлено на ${pluralN(в.deals, 'сделку', 'сделки', 'сделок')} клиента`}>
              <div className="t-label">{ВИД_ДОКУМЕНТА[в.kind] ?? в.kind}</div>
              <div className="t-sum">{fmtMoney(в.sum_doc ?? 0)}</div>
              <div className="t-label">
                {pluralN(в.n, 'документ', 'документа', 'документов')}
                {в.n_posted < в.n && `, проведено ${в.n_posted}`}
              </div>
            </div>
          ))}
          {покрытие && (
            <div className="tile" title="У скольких сделок клиента вообще нашлись документы 1С">
              <div className="t-label">Сделок с документами</div>
              <div className="t-sum">{покрытие.deals_with_docs}</div>
              <div className="t-label">из {покрытие.deals_total} у клиента</div>
            </div>
          )}
        </div>

        {строки.length === 0 ? (
          <div className="empty" style={{ marginTop: 12 }}>
            <div className="empty-title">
              Документов 1С под сделками {название} нет
            </div>
            <div className="empty-hint">
              Это НЕ «отгрузки не было». Документ попадает сюда только через номер сделки
              Bitrix в справочнике сделок 1С; оформленный без сделки — не попадёт никогда.
              Зеркало заказов клиента ведут с 23.04.2026, реализаций — с 12.05.2026: раньше
              этих дней самой базы УТ не существовало.
            </div>
          </div>
        ) : (
          /* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). */
          <div className="table-wrap cards" style={{ marginTop: 12 }}>
            <table>
              <thead>
                <tr>
                  <th className="nowrap">Вид</th>
                  <th className="nowrap">Номер</th>
                  <th className="nowrap">Дата</th>
                  <th>Статус</th>
                  <th className="nowrap">Сделка</th>
                  <th className="num">Сумма</th>
                </tr>
              </thead>
              <tbody>
                {строки.map((r) => (
                  <tr key={r.ref_key}>
                    <td className="nowrap" data-label="Вид">{ВИД_ДОКУМЕНТА[r.kind] ?? r.kind}</td>
                    <td className="mono nowrap" data-label="Номер">{fmtText(r.number)}</td>
                    <td className="nowrap" data-label="Дата">{fmtDate(r.doc_date)}</td>
                    <td data-label="Статус">
                      {fmtText(r.status)}
                      {!r.posted && <div className="hint">не проведён</div>}
                    </td>
                    <td className="nowrap" data-label="Сделка">
                      <EntityLink kind="deal" id={r.deal_id}
                                  title={r.deal_id ? `#${r.deal_id}` : null} />
                    </td>
                    <td className="num" data-label="Сумма">{fmtMoney(r.sum_doc ?? 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Весь список контрагентов, а не «главный»: спросить 1С по одной ссылке значит
            получить часть, и об этом ядро пишет прямым текстом в data_notes. */}
        {контрагенты.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div className="hint" style={{ marginBottom: 6 }}>
              Документы клиента оформлены
              на {pluralN(контрагенты.length, 'контрагента 1С', 'контрагентов 1С',
                          'контрагентов 1С')}. Названий в зеркале документов нет — там только
              ссылки 1С, поэтому показаны ссылки. Спрашивать 1С по одной из них значит
              получить часть.
            </div>
            <div className="table-wrap cards">
              <table>
                <thead>
                  <tr>
                    <th>Ссылка контрагента 1С</th>
                    <th className="num">Документов</th>
                    <th className="num">Сделок</th>
                    <th className="num">На сумму</th>
                  </tr>
                </thead>
                <tbody>
                  {контрагенты.map((к) => (
                    <tr key={к.ref}>
                      <td className="mono" data-label="Ссылка контрагента 1С">{к.ref}</td>
                      <td className="num" data-label="Документов">{к.n}</td>
                      <td className="num" data-label="Сделок">{к.deals}</td>
                      <td className="num" data-label="На сумму">{fmtMoney(к.sum_doc ?? 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Оговорки — словами ядра, а не пересказом: у них один автор, и он в ut_api.py. */}
        {доки.data?.data_notes && (
          <ul className="hint" style={{ marginTop: 8, paddingLeft: 18 }}>
            {(['link_is_indirect', 'docs_without_deal', 'several_contragents',
               'deal_bridge_text'] as const)
              .filter((к) => доки.data?.data_notes?.[к])
              .map((к) => <li key={к}>{доки.data!.data_notes[к]}</li>)}
          </ul>
        )}
      </QueryState>
    </div>
  )
}

// ═══════ расхождения состава у ответственного (`/products/mismatches?assigned=`) ═══════

/**
 * Сделки, где итог по товарным строкам не сходится с суммой сделки.
 *
 * ПОЧЕМУ БЛОК ПРО ЧЕЛОВЕКА, А НЕ ПРО КЛИЕНТА, И ПОЧЕМУ ТАК И ПОДПИСАН. У ручки есть отбор
 * по ответственному (`assigned`) и НЕТ отбора по компании. Назвать этот список
 * «расхождения по клиенту» значило бы соврать в заголовке: там сделки ответственного за
 * карточку целиком, включая чужих клиентов. Появится `company` у ручки — блок станет про
 * клиента, и это будет правка одной строки.
 *
 * СВОДКА В ЭТОМ ОТВЕТЕ НЕ СУЖАЕТСЯ НИЧЕМ — так её отдаёт ядро, и так и надо: она отвечает
 * на вопрос «из чего сложились 2,79 млрд». Но рядом со списком одного человека она читается
 * как его итог, и разница огромна: `assigned=6271` даёт 67 строк, а `summary.deals`
 * в том же ответе — 8 693 сделки на 2,79 млрд ₽ (замер боевого ядра 28.07.2026). Поэтому поле
 * `summary_scope` показано СЛОВАМИ ЯДРА над цифрами, а не пересказано здесь.
 *
 * Дверь та же, что у разрывов: `rights.проверить_чтение_свода` — сводка считается по всем
 * сделкам сразу, и роль со своей зоной получит 403 независимо от `assigned`.
 */
function РасхожденияОтветственного({ ответственный, имя }: {
  ответственный: string
  имя: string
}) {
  const права = useRightsApi()
  if (права.loaded && (права.denied('deals') || права.scoped('deals'))) {
    return <РасхожденияНеОткрыты свой={права.scoped('deals')} основание={права.basis} />
  }
  if (!ответственный) {
    return (
      <div className="card">
        <div className="page-head" style={{ marginBottom: 10 }}>
          <h2 style={{ margin: 0 }}>Расхождения состава у ответственного</h2>
        </div>
        <div className="empty">
          <div className="empty-title">Спрашивать нечем: у карточки нет ответственного</div>
          <div className="empty-hint">
            Отбор у этой ручки только по ответственному, а в карточке клиента поле пустое
            (в Bitrix «привязки нет» записано нулём). Запрос не отправлялся: без имени он
            вернул бы расхождения по всей базе под подписью «его сделки».
          </div>
        </div>
      </div>
    )
  }
  return <РасхожденияЧеловека ответственный={ответственный} имя={имя} />
}

function РасхожденияНеОткрыты({ свой, основание }: { свой: boolean; основание: string }) {
  return (
    <div className="card">
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Расхождения состава у ответственного</h2>
      </div>
      <div className="empty">
        <div className="empty-title">Этот свод вам не открыт</div>
        <div className="empty-hint">
          {свой
            ? 'Расхождение состава с суммой считается по всем сделкам сразу — сузить его до'
              + ' вашей зоны нечем. Отбор по ответственному у ручки есть, но дверь смотрит'
              + ' на зону, а не на него.'
            : 'Раздел сделок закрыт вашей роли, а расхождения состава — это деньги сделок.'}
          {основание ? ` Ваша зона: ${основание}.` : ''}
          {' '}Запрос отсюда не отправлялся вовсе.
        </div>
      </div>
    </div>
  )
}

function РасхожденияЧеловека({ ответственный, имя }: { ответственный: string; имя: string }) {
  // `kind=error` — то, что действительно надо чинить. Тот же выбор по умолчанию, что
  // в разделе «Расхождения состава». Без него в списке 8 693 строки (на 28.07.2026), из
  // которых 81 % не ошибка вовсе: сумму старых сделок не заводили, работы в состав
  // не вписывают.
  const q = useРасхождения(useMemo(
    () => ({ assigned: ответственный, kind: 'error' as const, limit: RELATED }), [ответственный],
  ))
  const строки = q.data?.rows ?? []
  const всего = q.data?.total ?? 0
  const сводка = q.data?.summary

  return (
    <div className="card">
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Расхождения состава у ответственного</h2>
        <div className="spacer" />
        <Link to="/mismatches?kind=error">раздел «Расхождения состава» →</Link>
      </div>

      {/* Что именно сужено, а что нет — НАД цифрами. Иначе список одного человека и итог
          по всей базе стоят рядом и читаются как одно. */}
      <div className="notice" style={{ marginBottom: 10 }}>
        Это сделки <b>{имя}</b> как ответственного, а не только сделки этого клиента: отбора
        по компании у ручки нет вовсе. Показаны расхождения степени «чинить» — из всех
        расхождений зеркала настоящих ошибок меньше пятой части.
      </div>

      <QueryState isLoading={q.isLoading} error={q.error}>
        <div className="tiles">
          <div className={`tile${всего > 0 ? ' hot' : ''}`}
               title="Сделки этого ответственного, где состав заведён неверно — счётчик ядра">
            <div className="t-label">Чинить у него</div>
            <div className="t-sum">{всего}</div>
            <div className="t-label">точное число по отбору</div>
          </div>
          {/* Плитки сводки НЕ сужены — это сказано и в подписи под ними, и словами ядра
              ниже. Показываем их всё равно: без знаменателя «67 из 8 693» (на 28.07.2026)
              число слева выглядит как весь масштаб беды. */}
          {(сводка?.by_severity ?? []).map((к) => (
            <div className="tile" key={к.severity}>
              <div className="t-label">
                {к.severity === 'error' ? 'Всего чинить' : к.severity === 'check'
                  ? 'Всего посмотреть' : 'Всего так ведут учёт'}
              </div>
              <div className="t-sum">{сколькоСделок(к.deals)}</div>
              <div className="t-label">по всей базе, не по отбору</div>
            </div>
          ))}
        </div>

        {строки.length === 0 ? (
          <div className="empty" style={{ marginTop: 12 }}>
            <div className="empty-title">
              Сделок с неверно заведённым составом у {имя} нет
            </div>
            <div className="empty-hint">
              Это про степень «чинить». Расхождения других степеней (сумму не заводили,
              разница меньше 5 %) у него быть могут — они не ошибка, и блок про них молчит
              намеренно. Весь список — в разделе «Расхождения состава».
            </div>
          </div>
        ) : (
          /* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). */
          <div className="table-wrap cards" style={{ marginTop: 12 }}>
            <table>
              <thead>
                <tr>
                  <th>Сделка</th>
                  <th>Что не так</th>
                  <th className="num">Сумма сделки</th>
                  <th className="num">Разница</th>
                </tr>
              </thead>
              <tbody>
                {строки.map((r) => (
                  <tr key={r.deal_id}>
                    <td data-label="Сделка">
                      <EntityLink kind="deal" id={r.deal_id} title={r.title || `#${r.deal_id}`} />
                    </td>
                    <td data-label="Что не так">
                      <span className="badge lose">{r.why}</span>
                    </td>
                    <td className="num" data-label="Сумма сделки">
                      {fmtMoney(r.opportunity, r.currency || 'RUB')}
                    </td>
                    <td className="num" data-label="Разница">
                      {fmtMoney(r.delta, r.currency || 'RUB')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Слова ядра, а не наш пересказ: у формулировки один автор — products_api.py. */}
        {q.data?.summary_scope && (
          <div className="hint" style={{ marginTop: 10 }}>
            <b>Про плитки справа:</b> {q.data.summary_scope}.
            {сводка && <> Целиком это {сколькоСделок(сводка.deals)}
              {' '}на {fmtMoney(сводка.money)} — против {сколькоСделок(всего)} слева.</>}
          </div>
        )}
        <div className="hint" style={{ marginTop: 8 }}>
          Причины и степени считает ядро тем же кодом, что раздел «Расхождения состава», —
          своего расчёта здесь нет. Чинится всё это в Bitrix, в самой сделке: на товарных
          строках висят бизнес-процессы, и правка из зеркала запускает их.
          {всего > строки.length
            && ` Показаны первые ${строки.length} из ${всего} — полный список в разделе.`}
        </div>
      </QueryState>
    </div>
  )
}

// ═════════ качество данных по клиенту (`/dq/rows?code=…&company=`) ═════════

/**
 * Три проверки качества данных, суженные до этого клиента.
 *
 * ПОЧЕМУ ТРИ, А НЕ ПОЛСОТНИ. Каждая проверка — отдельный запрос: сузить можно только СТРОКИ
 * (`/dq/rows`), а свод по всем проверкам (`/dq/checks`) сузить нечем — ядро отвечает на
 * `?company=` четырёхсотым нарочно, потому что это агрегаты по всей базе. Полсотни проверок
 * дали бы полсотни обращений на открытие карточки. Выбраны те, что про самого клиента: два
 * его флота и один про связь с людьми. Из 56 проверок отбор по компании понимают 20 —
 * какие именно, видно в `check.narrow` их же ответа.
 *
 * СУЖЕНИЕ НАСТОЯЩЕЕ, а не фильтр по загруженной странице: ядро считает и строки, и `total`
 * ОДНИМ условием. Замер боевого ядра 28.07.2026 — «дубли судов по IMO» по всей базе 388
 * находок, по клиенту 3262 их 28.
 *
 * Дверь ролевая: раздел 'data-quality' открыт группе «бухгалтерия» (три проверки раздела
 * читают строки банковской выписки). Роли без него ручка ответит 403 — запрос не уходит.
 */
const ПРОВЕРКИ_КЛИЕНТА: { code: string; зачем: string }[] = [
  { code: 'vessel-dup-imo',
    зачем: 'история сделок по судну расползается по нескольким карточкам' },
  { code: 'vessel-dup-marked',
    зачем: 'человек уже пометил карточку дублем, но свести её не свели' },
  { code: 'contact-no-way-to-reach',
    зачем: 'контакт есть, а позвонить и написать некуда' },
]

function КачествоПоКлиенту({ companyId, название }: { companyId: string; название: string }) {
  const права = useRightsApi()
  const закрыт = права.loaded && права.denied('data-quality')

  return (
    <div className="card">
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Качество данных по этому клиенту</h2>
        <div className="spacer" />
        <Link to="/data-quality">раздел «Качество данных» →</Link>
      </div>

      {закрыт ? (
        <div className="empty">
          <div className="empty-title">Раздел «Качество данных» вашей роли недоступен</div>
          <div className="empty-hint">
            Это отказ по правам, а не «находок нет»: три проверки раздела читают строки
            банковской выписки, и весь раздел поэтому открыт бухгалтерии. Запрос отсюда
            не отправлялся.
          </div>
        </div>
      ) : (
        <>
          <div className="notice" style={{ marginBottom: 10 }}>
            Отбор по клиенту <b>настоящий</b>: и строки, и счётчик ядро считает одним условием
            по его сделкам и контактам, а не фильтрует загруженную страницу. Проверок здесь
            три, а не полсотни: каждая — отдельный запрос, а свод по всем сузить нечем.
          </div>
          {ПРОВЕРКИ_КЛИЕНТА.map((п) => (
            <ПроверкаПоКлиенту key={п.code} code={п.code} зачем={п.зачем}
                               companyId={companyId} название={название} />
          ))}
        </>
      )}
    </div>
  )
}

/** Одна проверка. Свой запрос у каждой — ручка отвечает по одному коду за раз. */
function ПроверкаПоКлиенту({ code, зачем, companyId, название }: {
  code: string
  зачем: string
  companyId: string
  название: string
}) {
  const q = useСтрокиПроверки(code, 0, true, useMemo(() => ({ company: companyId }), [companyId]))
  const д: ОтветСтрокСужением | undefined = q.data
  const строки = д?.rows ?? []
  const всего = д?.total ?? 0
  const паспорт = д?.check
  /**
   * Страница вместила всю выборку. false — судить по строкам «такого нет» НЕЛЬЗЯ, и ядро
   * говорит об этом дважды: флагом и оговоркой `page_is_partial`. Обе показаны.
   */
  const полно = д?.page_complete !== false
  const сужено = !!д?.narrowed_by && Object.keys(д.narrowed_by).length > 0

  return (
    <div style={{ marginTop: 12 }}>
      <QueryState isLoading={q.isLoading} error={q.error}>
        <>
          <div className="page-head" style={{ marginBottom: 6 }}>
            <b>{паспорт?.title ?? code}</b>
            <div className="spacer" />
            {паспорт && (
              <span className={`badge ${классСтепени(паспорт.severity)}`}>
                {всего} {паспорт.unit}
              </span>
            )}
          </div>
          <div className="hint" style={{ marginBottom: 6 }}>
            Почему это важно: {зачем}. Чинится {паспорт?.fix_label ?? 'в источнике'}.
          </div>

          {строки.length === 0 ? (
            <div className="empty">
              <div className="empty-title">
                {полно
                  ? `У ${название} находок по этой проверке нет`
                  : 'Ответить «находок нет» нечем: страница неполная'}
              </div>
              <div className="empty-hint">
                {полно && сужено
                  ? 'Выборка сужена до этого клиента и целиком поместилась в страницу, поэтому'
                    + ' «нет» здесь означает именно «нет», а не «не поместилось».'
                  : полно
                    ? 'Ядро сужение по этой проверке не применило — судить по ней об одном'
                      + ' клиенте нельзя. Смотрите раздел «Качество данных».'
                    : 'Страница ядра ограничена, и выборка в неё не влезла. Разбирать надо'
                      + ' в разделе «Качество данных».'}
              </div>
            </div>
          ) : (
            /* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). */
            <div className="table-wrap cards">
              <table>
                <thead>
                  <tr>
                    <th className="nowrap">Запись</th>
                    <th>Что нашли</th>
                  </tr>
                </thead>
                <tbody>
                  {строки.slice(0, RELATED).map((r, i) => (
                    <tr key={String(r.id ?? i)}>
                      <td className="nowrap" data-label="Запись">
                        {паспорт?.link
                          ? <EntityLink kind={паспорт.link as 'vessel' | 'contact' | 'deal'}
                                        id={r.id as string | number}
                                        title={fmtText(r.title)} />
                          : fmtText(r.title ?? r.id)}
                      </td>
                      {/* Подписи полей — как их назвало ядро. Свой словарь «код → подпись»
                          разъехался бы с ним в день, когда в проверку добавят колонку. */}
                      <td data-label="Что нашли">
                        <span className="hint">
                          {Object.entries(r)
                            .filter(([к, v]) => к !== 'id' && к !== 'title'
                              && v !== null && v !== '')
                            .map(([к, v]) => `${к}: ${String(v)}`)
                            .join(' · ') || '—'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="hint" style={{ marginTop: 6 }}>
            {сужено
              ? `Число ${всего} посчитано по суженному множеству: это находки у этого клиента,`
                + ' а не по всему зеркалу.'
              : 'Сужение к этой проверке не применилось — число относится ко всему зеркалу.'}
            {' '}
            {полно
              ? 'Страница вместила выборку целиком, поэтому «нет находок» здесь можно читать'
                + ' буквально.'
              : `Страница НЕПОЛНАЯ: показано ${строки.length} из ${всего}, и судить по ней`
                + ' «такого больше нет» нельзя.'}
            {строки.length > RELATED
              && ` В таблице первые ${RELATED} — остальное в разделе «Качество данных».`}
          </div>
          {/* Оговорки ядра, а не наш пересказ. `page_is_partial` приходит ровно тогда, когда
              страница неполна, — вторая половина того же предупреждения. */}
          {д?.data_notes && (
            <ul className="hint" style={{ marginTop: 6, paddingLeft: 18 }}>
              {(['narrowed', 'page_is_partial', 'vessels_via_deals'] as const)
                .filter((к) => д.data_notes?.[к])
                .map((к) => <li key={к}>{д.data_notes[к]}</li>)}
            </ul>
          )}
        </>
      </QueryState>
    </div>
  )
}
